/**
 * LA NAVIGAZIONE DELL'APPLICATIVO — **una sola definizione**, per la colonna desktop e per il
 * cassetto mobile.
 *
 * ## Perché sta qui e non in due file
 *
 * Fino al 31-08-2026 l'albero era scritto due volte: una in `DesktopSidebar`, una in
 * `MobileNavDrawer`. Le due copie sono partite identiche e sono divergite, come fanno sempre:
 * al momento dell'unificazione al telefono mancavano **tredici voci** — l'intero blocco «Config
 * commesse & lavoro» di Amministrazione, più Promemoria timbrature, Policy ritardi, Status
 * protetti, Attributi tenant, Documenti del portale, Argomenti assistenza, Job schedulati,
 * Manutenzione sistema e Tariffario km. Nessuno le aveva togliete: erano state aggiunte da una
 * parte e non dall'altra.
 *
 * Il difetto non era la mancanza: era che **nessuno poteva accorgersene**. Due elenchi di
 * novanta righe in due file diversi non si confrontano a occhio, e chi aggiunge una pagina non
 * ha modo di sapere che deve toccare due posti. Con una definizione sola il problema non esiste
 * più, e `tests/unit/navParita.test.ts` fa da guinzaglio sul poco che resta separato.
 *
 * ## Cosa resta diverso fra i due, e perché è scritto nel dato
 *
 * Oggi niente. Una voce che c'è solo sul telefono si dichiara con `soloMobile: true`, nel dato e
 * non duplicando l'albero; l'ultima, «Approvazioni prenotazioni», è stata tolta il 23-10-2026
 * insieme alla coda vecchia che apriva.
 *
 * ## Criterio dei gruppi
 *
 * Un gruppo risponde a **una domanda di chi lo apre** e sta in una schermata senza scorrere.
 * Dove ne mescolava due, è separato: «Il mio lavoro» conteneva anche Progetti, Clienti e
 * Magazzino, che non sono il mio lavoro ma l'anagrafica su cui lavoro; «Insights & report»
 * conteneva Fatturazione, Ordini e Prodotti, che non sono report ma il ciclo commerciale;
 * «Ticket» stava in Contenuti, dove non c'entra niente.
 *
 * L'albero elenca tutte le pagine navigabili, escluse le rotte di dettaglio con parametro e i
 * flussi di sistema.
 */
import type { ComponentType } from 'react';
import {
  IconAddressBook, IconAdjustments, IconArrowsExchange, IconArticle, IconBeach, IconBook,
  IconBrandAndroid, IconBriefcase, IconBuildingCommunity, IconBuildingFactory2, IconBuildingSkyscraper,
  IconCalendar, IconCalendarStats, IconCar, IconCategory, IconChartBar, IconChartDonut,
  IconChartPie, IconChecklist, IconClipboardCheck, IconClipboardList, IconClockHour4, IconCoin,
  IconCalendarDue, IconUsersGroup, IconCreditCard, IconFileCertificate, IconFileSpreadsheet, IconFileText, IconFlask,
  IconFolders, IconGauge, IconKey, IconGavel, IconGitBranch, IconHeadset,
  IconHistory, IconHome, IconInfoCircle, IconLanguage, IconLayoutGrid,
  IconLayoutKanban, IconLicense, IconLifebuoy, IconList, IconListSearch,
  IconNfc, IconNotebook, IconPackage, IconPhoto, IconPlaneTilt, IconPlugConnected, IconReceipt,
  IconReceipt2, IconRefresh, IconReportAnalytics, IconRocket, IconScale, IconSettings,
  IconShieldLock, IconSitemap, IconSpeakerphone, IconTags, IconTargetArrow, IconTemplate, IconToggleLeft,
  IconUserCheck, IconUserCircle, IconUserShield, IconUsers, IconWorld, type IconProps,
  IconRobot, IconChecks,
} from '@tabler/icons-react';
import { ORGANIZATION_SUBJECTS, type OrganizationSubject } from '@/features/organization/setupSteps';

export interface NavItem {
  label: string; to: string; icon: ComponentType<IconProps>;
  /**
   * ⚠️ **La pagina non c'è ancora, e la voce sì.**
   *
   * Serve alle sezioni che si stanno costruendo: la colonna dice **di cosa è fatta** la sezione
   * invece di crescere una riga alla volta sotto le mani di chi la usa, e una voce spenta si
   * legge «non ancora» mentre una voce assente non si legge affatto.
   *
   * `to` è l'indirizzo che quella pagina **avrà**: dichiararlo adesso vuol dire che il giorno in
   * cui esiste si cancella una parola sola, e che nel frattempo si sa dove andrà. Le guardie che
   * pretendono una rotta viva (`navGrouping`) e la presenza in ⌘K (`navCatalogoUnico`) saltano
   * queste voci — con la rotta vera al suo posto tornano sorvegliate da sole.
   *
   * Chi la disegna non ci mette un link: né la colonna né il cassetto la rendono cliccabile.
   */
  future?: true;
  badgeKey?: 'sw' | 'leave' | 'tasks' | 'approvals'; flag?: string;
  /**
   * Come `flag`, ma basta **uno** dei moduli elencati: per una pagina che mette insieme più moduli
   * e regge con uno solo acceso. Oggi «Prenotazioni», che legge auto, trasferte e sale ognuna col
   * suo modulo: un'azienda con le sole sale deve poterla aprire.
   */
  flagAny?: string[];
  /**
   * Un **allarme**: quanti casi stanno facendo danno adesso — oggi, le persone senza turnazione.
   * Diverso da `badgeKey`, che conta cose da approvare, e dal puntino delle segnalazioni, che non
   * ha numero. Il conto e chi lo vede li decide `DesktopSidebar` (`useNavAlarms`).
   */
  alarmKey?: NavAlarmKey;
  /*
   * ⚠️ Qui c'era `dot?: boolean`, e non è stato tolto per pulizia: era acceso su
   * «Rendicontazione», decorativo, e i puntini del menu adesso si **derivano** dalle
   * segnalazioni del server (`features/admin/avvisiMenu.ts`). Il campo non c'è più perché un
   * puntino scritto a mano qui dentro non deve compilare: in Lares tutte e venti le voci ne
   * avevano uno messo senza criterio, e il segnale vero non si distingueva più — la funzione
   * c'era, sembrava accesa, e non poteva funzionare.
   */
  /**
   * Claim richiesto per **vedere** la voce. Diverso da `flag`, che è un interruttore di
   * tenant: qui si tratta di permessi della persona. Il server resta l'autorità — la pagina
   * gestisce comunque il 403 — ma una voce che per la maggioranza degli utenti finisce
   * sempre in «non autorizzato» è solo rumore nel menu.
   *
   * ## Come si scegli il claim di una voce — la trappola del `.read`
   *
   * I claim CRUD del BE si chiamano `<ClasseCLR>.<azione>`, e **il `.read` delle risorse di
   * lookup è nel profilo base di ogni dipendente**: `ErpEmployee.read`, `ErpSite.read`,
   * `ErpRole.read`, `Vehicle.read`, `MeetingRoom.read` servono a compilare i form, quindi li ha
   * chiunque. Gatare una pagina di **gestione** sul `.read` della sua risorsa non nasconde
   * niente — è il controllo che sembra esserci e non c'è. La regola che segue questo albero:
   *
   *  · pagina **self-service** (i propri record) → il `.read` della risorsa
   *    (`/leave` → `LeaveRequest.read`);
   *  · pagina di **gestione o configurazione** → la **scrittura** (`/organization/sites` →
   *    `ErpSite.update`, `/organization/leave-config` → `LeaveType.update`);
   *  · vista **di tutta l'azienda** → il claim `readAll` del modulo
   *    (`/insights` → `TimesheetEntry.readAll` & co.);
   *  · **capacità** che non passa dal CRUD → il claim funzionale (`/warehouse` operativo →
   *    `warehouse.operate`, `/support-intake` → `support.intake.work`);
   *  · pagina **legacy MaeWay** senza claim CRUD in nessun profilo → il claim di rotta
   *    `r-p-*` (`/articles` → `r-p-articles`), che a DB esiste ed è assegnato ai ruoli vecchi.
   *
   * ⚠️ Il nome è quello della **classe .NET**, non il type JSON:API: `ProjectEntity.read` gatera
   * `GET /api/projects`, e le presenze org-wide sono `Attendance.readAll` mentre il CRUD è
   * `ErpAttendance.*`. Un claim scritto male è un controllo sempre falso, cioè una voce
   * invisibile a tutti: `tests/unit/navMatriceProfili.test.ts` è lì per accorgersene.
   */
  claim?: string;
  /**
   * Come `claim`, ma basta **uno** dei claim elencati. Serve dove la stessa pagina è aperta da
   * autorizzazioni diverse e non gerarchiche: il perimetro multi-azienda si allarga con
   * `canSeeAllTenants` (il gruppo) **oppure** con `canManageSubTenants` (le sotto-aziende), e
   * gaterare su uno solo dei due avrebbe nascosto la voce a metà di chi ne ha diritto.
   */
  claimAny?: string[];
  /**
   * `true` = questa voce **non ha** un gate, e non è una dimenticanza: è una pagina che serve a
   * chiunque sia entrato (la Home, le impostazioni del proprio profilo, l'aiuto, le circolari).
   *
   * Esiste perché `tests/unit/navGating.test.ts` pretende che ogni voce dichiari qualcosa: senza
   * un modo di dire «visibile per scelta», l'unica alternativa era una lista di eccezioni in un
   * file lontano da qui, che nessuno aggiorna quando aggiunge una pagina.
   */
  alwaysVisible?: true;
  /**
   * La voce si vede solo a chi **approva** qualcosa (23-10-2026, «Approvazioni»).
   *
   * Non è un permesso: il server non dice «questa persona approva», e chi approva per una delega
   * può non avere nessun claim. Decide `useApprovalQueues().visible` — superadmin, HR, responsabile
   * di qualcuno, oppure almeno una richiesta in coda — e lo applicano i due menu. La **pagina** resta
   * aperta a chiunque (`alwaysVisible`): a chi non ha niente da approvare dice che non ha niente.
   */
  approversOnly?: true;
  /**
   * Il claim che serve a questa pagina e che **il catalogo del BE non concede** a chi ne ha bisogno.
   *
   * Non è un gate: è un debito dichiarato nel dato invece che in un commento. Serve perché una
   * pagina che resta al solo super-admin, senza una riga che dica perché, al giro dopo si legge
   * come un guasto — e qualcuno la "aggiusta" togliendo il gate.
   *
   * ⚠️ **Il riferimento è `RoleClaimCatalog`, non il database del dev**, e la distinzione è il
   * motivo per cui questo campo esiste su voci che sul dev si aprono benissimo. Il catalogo è ciò
   * che un **tenant nuovo** riceve al provisioning; il DB del dev ha anni di claim aggiunti a mano,
   * e le due cose sono divergenti in entrambe le direzioni (misurato: il dipendente base ha 204
   * claim sul dev contro i 180 che il catalogo produce). Una pagina che funziona qui e non
   * funzionerà dal primo cliente nuovo è il difetto che questo campo tiene scritto.
   *
   * Tre forme, tutte richieste al BE e non errori nostri:
   *
   *  · il claim **non è a catalogo per nessuno** — `ProjectTypePreset.update`: nessun profilo
   *    scrive i preset di progetto (`ProjectCategory.update` era in questo stato fino al
   *    22-10-2026, poi il BE l'ha dato a ProjectManager e il gate è stato tolto);
   *  · a catalogo è su un **profilo che non viene seedato** — era `VehicleMaintenance.read` su
   *    `FleetManager`, finché il BE non l'ha collegato il 02-11-2026 (task 3582);
   *  · il BE lo **legge e non lo seeda** — `canManageSprints`, da assegnare a mano.
   *
   * Va insieme al gate, non al posto suo: la pagina si gata su un claim vero, così il giorno che il
   * BE lo concede la voce compare da sé. `tests/unit/navGating.test.ts` pretende che ogni valore
   * sia nominato in `docs/FE-TO-BE-CLAIM-GAPS-MENU-v1.md`: un debito che resta in un campo
   * TypeScript non è una richiesta, è un promemoria che nessuno leggerà.
   */
  missingGate?: string;
  /**
   * Nome del cliente per cui questa pagina è stata sviluppata **su misura**.
   *
   * Serve a distinguerla dal prodotto: chi apre il menu di un'azienda che ha comprato del
   * custom deve poter vedere, senza chiederlo a nessuno, quali voci non troverà altrove. La
   * voce prende una velatura che respira (`.mw-nav-custom` in `mobile/theme.css`) e un pallino
   * accanto all'etichetta — il pallino perché il colore di fondo, da solo, non si legge in
   * tema scuro né lo coglie chi non distingue quella tinta.
   */
  custom?: string;
  /**
   * `true` = la voce si vede **solo nel cassetto mobile**.
   *
   * Non è una scorciatoia per tenersi due alberi: è il caso in cui la stessa pagina, sul desktop,
   * è raggiungibile da un'altra strada — una scheda dentro la sezione — che su telefono non
   * esiste. Metterla anche nella colonna desktop sarebbe una riga in più per una pagina che da lì
   * si apre già; toglierla dal menu mobile la renderebbe irraggiungibile.
   *
   * Se ne dovessero comparire molte, è il segno che le due navigazioni stanno divergendo di nuovo
   * e la domanda da farsi è un'altra: perché quella pagina su telefono non ha una strada propria?
   */
  soloMobile?: boolean;
  /**
   * Il guscio in cui questa voce esiste: `'web'` = solo nel browser, `'app'` = solo dentro
   * all'applicazione installata.
   *
   * ⚠️ **Non e' `soloMobile`**, ed e' la distinzione che serve avere chiara. `soloMobile` parla
   * della **larghezza dello schermo**: il cassetto invece della colonna, e vale anche in un
   * browser stretto. Questo parla del **guscio**: Cordova invece di una scheda del browser, e
   * vale anche su un tablet largo.
   *
   * Serve per le pagine che nell'altro guscio non hanno senso, non per quelle che stanno strette:
   * «scarica l'applicazione» dentro all'applicazione, o una schermata che vive di NFC aperta dal
   * browser. Il caso opposto — una pagina che il guscio non sa mostrare — e' lo stesso campo dal
   * verso opposto.
   *
   * ⚠️ Oggi **nessuna voce lo usa**, ed e' dichiarato di proposito invece che lasciato al
   * momento del bisogno: il predicato lo applica gia' e `routeAccess.test.ts` lo esercita in
   * tutti e due i versi. La differenza fra questo e una promessa vuota sta li' — `masterOnly`
   * era un messaggio che nessun codice poteva produrre, questo e' codice che funziona e aspetta
   * un dato.
   */
  runtime?: 'web' | 'app';
}
/** Blocco etichettato **dentro** un gruppo: il secondo livello di Amministrazione. */
export interface NavSubGroup {
  title: string;
  items: NavItem[];
  /**
   * L'icona dell'**area**, usata da `/administration` — non dalla colonna.
   *
   * ⚠️ Nella colonna resta la regola di sopra: un'icona per gruppo, non per riga, o l'occhio
   * non trova più il gruppo in una parete di simboli. Nella panoramica le aree sono sette
   * riquadri affiancati, e lì l'icona è il punto d'appoggio che distingue l'uno dall'altro —
   * insieme al colore d'identità, che da solo non basta a chi non lo percepisce.
   */
  icon?: ComponentType<IconProps>;
}
export interface NavSection {
  title?: string;
  /**
   * L'icona del **gruppo**. Sta qui e non sulle voci: con un'icona per riga la colonna
   * diventava una parete di simboli tutti uguali di peso, in cui l'occhio non trovava più il
   * gruppo. Una sola icona per gruppo dà il punto d'appoggio, e le voci dentro si leggono
   * come una lista di parole — che è quello che sono.
   *
   * Le voci conservano comunque il proprio `icon`: serve altrove (ricerca, scorciatoie) e
   * toglierlo dal dato significherebbe perderlo.
   */
  icon?: ComponentType<IconProps>;
  items?: NavItem[];
  /**
   * Sottogruppi, alternativa a `items` per i gruppi troppo grossi per una lista piatta.
   *
   * Amministrazione aveva **42 voci** in fila: un elenco così non si legge, si scorre finché
   * non si riconosce una parola. Il secondo livello non aggiunge un click (il gruppo è già a
   * fisarmonica) ma dà alla scansione dei punti d'appoggio: si cerca il blocco, non la riga.
   */
  groups?: NavSubGroup[];
  /**
   * ⚠️ **La colonna non disegna le voci di questo gruppo: disegna una riga sola, che porta qui.**
   *
   * Serve al gruppo che è diventato troppo grande per una colonna. «Configurazione» ha sette
   * aree e una quarantina di pagine: da chiuso era una riga che non diceva niente, da aperto una
   * colonna più lunga dello schermo in cui si cercava a memoria — e la ricerca ⌘K era diventata
   * il modo vero di arrivarci, cioè il menu aveva smesso di funzionare.
   *
   * Le aree e le voci **restano qui**: sono i dati che la pagina indice legge per disegnarsi, e
   * sono anche il modo in cui ⌘K e le guardie sanno che quelle pagine esistono. Cambia solo chi
   * le mostra.
   *
   * ⚠️ La riga si vede se **almeno una** delle voci dentro è visibile a chi guarda. Non un claim
   * scritto a mano: una lista di permessi da tenere allineata a quaranta voci diverge al primo
   * cambio, e il modo di fallire è una voce che porta a una pagina vuota — o, peggio, nascosta a
   * chi invece ci poteva entrare.
   */
  indexOnly?: string;
  /**
   * Il gruppo si mostra **solo sull'azienda di piattaforma** (il Master), deciso il 03-11-2026 per
   * «Configurazione»: sono le impostazioni del prodotto, e nelle aziende dei clienti non si
   * offrono dal menu. Vale anche per il superadmin entrato in un'azienda cliente.
   *
   * ⚠️ Nasconde il **menu**, non le pagine: fuori dal Master restano apribili dagli altri
   * collegamenti (la casella delle circolari porta al registro, Licenze ai moduli…), da ⌘K e
   * dall'indirizzo, e ognuna risponde coi permessi di sempre.
   */
  masterOnly?: boolean;
}

/** Tutte le voci di un gruppo, appiattite: serve a badge, filtro di visibilità e voce attiva. */
export const sectionItems = (sec: NavSection): NavItem[] =>
  (sec.items ?? []).concat(...(sec.groups ?? []).map((g) => g.items));

/**
 * Da soggetto dell'organizzazione a voce di menu.
 *
 * ⚠️ Il menu **non riscrive** l'elenco: lo legge. Le voci di «Organizzazione» e i passi del
 * percorso di configurazione sono la stessa lista (`features/organization/setupSteps.ts`), e
 * due copie di una lista divergono — in questo repo e' gia' successo tre volte fra menu
 * desktop e cassetto mobile, l'ultima con tredici voci di scarto.
 */
function navItemOf(s: OrganizationSubject): NavItem {
  return {
    label: s.label,
    to: s.to,
    icon: s.icon,
    ...(s.claim ? { claim: s.claim } : {}),
    ...(s.claimAny ? { claimAny: [...s.claimAny] } : {}),
    ...(s.missingGate ? { missingGate: s.missingGate } : {}),
    ...(s.flag ? { flag: s.flag } : {}),
    ...(s.future ? { future: true as const } : {}),
    ...(s.alarmKey ? { alarmKey: s.alarmKey } : {}),
  };
}

/** Chi guida un gruppo, chi è dell'ufficio del personale, chi amministra le impostazioni. */
const TEAM_LEAD_CLAIMS = [
  'canConfigureOwnGroupObligations', 'canConfigureOwnGroupTasks', 'isHR', 'TenantSetting.update',
] as const;

/** Chi lavora sull'anagrafica di tutta l'azienda: erano i claim di «Segreteria & HR» (`/workforce`, tolta il 02-11-2026). */
export const HR_DESK_CLAIMS = ['UserProfile.readAll', 'ErpEmployee.update'] as const;

/** Chi guarda l'atteso del team: l'HR, chi legge le presenze di tutti, e chi è responsabile di qualcuno. */
export const TEAM_EXPECTED_CLAIMS = [...HR_DESK_CLAIMS, 'isHR', 'Attendance.readAll', 'isTeamLeader'] as const;

/**
 * Chi vede cosa in «Scadenze» (03-11-2026): ogni fonte col permesso della pagina da cui viene. La voce
 * di menu si apre a chi ne vede almeno una; la pagina accende le fonti una per una.
 */
export const DEADLINES_ACCESS = {
  /* Gli obblighi da fare: chi ha collaboratori, l'ufficio del personale, chi vede la pianificazione di tutti. */
  obligation: [...TEAM_LEAD_CLAIMS, 'PlanningEntry.readAll'],
  /* Corsi, patenti e fine dei rapporti: chi lavora sull'anagrafica di tutta l'azienda. */
  hr: [...HR_DESK_CLAIMS],
  /* Gli stati protetti (art. 9 GDPR): solo chi li gestisce. */
  protected: ['hr.protected.write', 'ErpEmployeeProtectedStatus.update'],
} as const;

/** Gli allarmi che una voce può portare. */
export type NavAlarmKey = 'missingRotations' | 'hierarchyGaps' | 'employeeGaps' | 'sitesWithoutTimes' | 'groupsWithoutArea' | 'pendingApprovals' | 'overdueDeadlines';

export const SECTIONS: NavSection[] = [
  /* Fuori dalla fisarmonica resta solo la Home: è il punto di ritorno, non una sezione.
     La Pianificazione è scesa in «Il mio lavoro», dove sta la domanda a cui risponde. */
  { items: [
    { label: 'Home', to: '/', icon: IconHome, alwaysVisible: true },
  ] },
  /*
   * ⚠️ «DP» SONO LE INIZIALI DI CHI STA RICOSTRUENDO QUESTO GRUPPO, E VANNO VIA.
   *
   * Non è un nome: è un cartello «lavori in corso». Il gruppo rifà l'ORGANIZZAZIONE di Lares — la
   * cui prima voce è `/company-master` — e nasce **accanto** a quello che c'è invece di
   * rimaneggiarlo, così le due strade convivono finché la nuova non è completa. Quando lo sarà, il
   * suffisso sparisce e le voci vecchie si tolgono.
   *
   * ⚠️ Sta **in cima**, sotto la Home, e non in fondo con le cose di amministrazione: qui c'è
   * l'azienda per cui si lavora, non la configurazione del prodotto.
   *
   * Il gruppo non ha un gate proprio: si nasconde da sé quando nessuna delle sue voci è visibile,
   * che è come funzionano tutti gli altri. È la voce a portare i claim — e li porta **in unione**,
   * non in intersezione: chi può cambiare i dati dell'azienda (`Tenant.update`) deve trovarla anche
   * se non vede il gruppo societario, e viceversa. Col claim più restrittivo la voce sparirebbe a
   * metà delle persone che ci lavorano.
   */
  /*
   * ⚠️ LE VOCI ARRIVANO DA `features/tenants/sezioneOrganizzazione.ts`, NON SONO SCRITTE QUI.
   *
   * Le stesse voci disegnano anche la striscia sopra il titolo delle pagine della sezione: due
   * elenchi divergono, e in questo repo è già successo tre volte — l'ultima con tredici voci di
   * scarto fra menu desktop e cassetto mobile. Chi aggiunge un soggetto lo aggiunge là, e
   * compare in tutti e due i posti.
   *
   * I soggetti senza indirizzo (Autorizzazioni, Modalità di lavoro) restano fuori dal menu: una
   * voce che non porta da nessuna parte, in una colonna, è solo una riga che delude. Nella
   * striscia ci sono, spente, perché lì dicono di cosa è fatta la sezione.
   */
  /* ⚠️ «DP» resta finché non lo toglie chi l'ha messo: è un cartello «lavori in corso», e il
     gruppo nuovo convive con quello di prima invece di sostituirlo. Rinominarlo di mia
     iniziativa il 23-09-2026 ha fatto sparire un riferimento che serviva ancora. */
  /* Dipendenti e Gerarchia stanno nel registro (sono passi del percorso) ma nel menu vanno in
     «Risorse umane» (`menuSection: 'hr'`, 03-11-2026). */
  { title: 'Organizzazione DP', icon: IconBuildingSkyscraper, items: ORGANIZATION_SUBJECTS.filter((s) => s.menuSection !== 'hr').map(navItemOf) },

  /*
   * RISORSE UMANE — il lavoro di tutti i giorni dell'ufficio del personale (23-10-2026).
   *
   * La **struttura** (sedi, mansioni, gerarchia, turni) sta in «Organizzazione DP»; qui c'è quel che
   * si guarda ogni giorno: chi c'è, le scadenze, le persone. Le approvazioni hanno la loro sezione.
   *
   * ⚠️ I permessi sono **quelli che le pagine avevano già**: l'indirizzo e la voce hanno la stessa
   * regola (`navAccess.ts`), quindi un claim diverso qui avrebbe chiuso o aperto la pagina a
   * qualcuno. `/workforce/*` erano governate da «Segreteria & HR».
   *
   * ⚠️ Fuori, per ora: il **calendario del team**. Lo apre ogni dipendente (da «Assenze»), quindi
   * una voce qui lo mostrerebbe a tutti o lo chiuderebbe a chi lo usa.
   *
   * Dal 03-11-2026 le voci sono meno, e più grandi: chi c'è oggi, le presenze per sede e l'atteso
   * del team sono le schede di una pagina sola («Presenze del personale»), il personale esterno è
   * un filtro di Dipendenti, e Dipendenti e Gerarchia arrivano qui da «Organizzazione DP».
   */
  { title: 'Risorse umane', icon: IconUsersGroup, items: [
    /* Tre pagine in una (03-11-2026): «Chi c'è oggi», «Presenze per sede» e «Atteso del team» erano
       tre voci che rispondevano alla stessa domanda. Ogni scheda si apre col permesso che la sua
       pagina aveva: l'atteso anche al responsabile di un gruppo, le altre due all'ufficio del
       personale. Gli indirizzi vecchi portano alla scheda giusta. */
    { label: 'Presenze del personale', to: '/hr/presence', icon: IconUserCheck, claimAny: [...TEAM_EXPECTED_CLAIMS] },
    /* «Scadenze» (03-11-2026, era «Scadenze del team»): obblighi, corsi, stati protetti, patenti e fine
       dei rapporti in un elenco, più la gestione dei corsi che era «Formazione». */
    { label: 'Scadenze', to: '/hr/deadlines', icon: IconCalendarDue, claimAny: [...DEADLINES_ACCESS.obligation, ...DEADLINES_ACCESS.hr, ...DEADLINES_ACCESS.protected], alarmKey: 'overdueDeadlines' },
    /* Il personale esterno è il filtro «Esterni» di Dipendenti (03-11-2026), con P.IVA e tariffa
       in colonna: la pagina a sé elencava gli stessi dati senza il nome della persona. */
    navItemOf(ORGANIZATION_SUBJECTS.find((x) => x.id === 'employees')!),
    navItemOf(ORGANIZATION_SUBJECTS.find((x) => x.id === 'hierarchy')!),
    /* Dati particolari del personale (art. 9 GDPR): li gestisce l'ufficio del personale, quindi
       stanno qui e non in «Organizzazione DP», che è la configurazione dell'azienda (01-11-2026).
       I permessi non cambiano: specialista e direzione HR (e il superadmin); l'amministratore no. */
    { label: 'Stati protetti', to: '/hr/protected-statuses', icon: IconShieldLock, claimAny: ['hr.protected.write', 'ErpEmployeeProtectedStatus.update'] },
    { label: 'Cruscotto HR', to: '/hr/dashboard', icon: IconChartBar, claimAny: [...HR_DESK_CLAIMS] },
  ] },
  /*
   * Tutte le code da approvare in una pagina a schede (23-10-2026): una sezione che porta alla sua
   * pagina (`indexOnly`), visibile solo a chi approva, subito sotto «Risorse umane» (23-10-2026). Il numero è un allarme e non un contatore:
   * una richiesta ferma blocca qualcun altro.
   */
  { title: 'Approvazioni', icon: IconChecks, indexOnly: '/approvals', items: [
    { label: 'Approvazioni', to: '/approvals', icon: IconChecks, alwaysVisible: true, approversOnly: true, alarmKey: 'pendingApprovals' },
  ] },

  { title: 'Presenze & assenze', icon: IconClockHour4, items: [
    // Da qui: l'atteso del team (link in testata).
    /* I `flag` qui sotto sono gli interruttori di modulo del BE (BE-CONTRACT-TENANT-MODULES-v1 §2):
       dal 11-09-2026 una rotta di un modulo spento risponde 403 a chiunque, quindi la voce si toglie
       **prima** che la pagina si apra a meta'. Il nome del flag e' la chiave del catalogo BE:
       `tests/unit/tenantModules.test.ts` fallisce su un flag che il catalogo non conosce. */
    { label: 'Presenze', to: '/attendance', icon: IconUserCircle, flag: 'attendance.enabled', claim: 'ErpAttendance.read' },
    // Da qui: la coda approvazioni smart working.
    { label: 'Smart Working', to: '/smart-working', icon: IconChecklist, badgeKey: 'sw', flag: 'smartWorking.enabled', claim: 'SmartWorkingRequest.read' },
    // Da qui: la coda approvazioni assenze e il calendario team.
    { label: 'Assenze', to: '/leave', icon: IconBeach, badgeKey: 'leave', flag: 'leave.enabled', claim: 'LeaveRequest.read' },
    /* Quarta voce, e sì: contraddice il «tre voci, non sette» qui sopra. La regola dice di non
       elencare le **sotto-pagine** di una schermata, e questa non lo è — è una configurazione
       che vive da sola, e stava sepolta in «Config presenze & assenze» dove chi gestisce i tag
       non la trovava. Spostata, non duplicata: la voce nel gruppo di amministrazione è stata
       rimossa, perché due strade nello stesso menu verso la stessa pagina sono il difetto che
       avevamo appena finito di togliere ai ticket. */
    { label: 'Punti di timbratura', to: '/admin/attendance-sources', icon: IconNfc, flag: 'attendance.enabled', claim: 'ErpAttendanceSource.update' },
    /* Quinta voce, self-service: chiedere a un collega di scambiare un turno e rispondergli. Non è una
       sotto-pagina di Presenze — ha un suo flusso a tre (collega, responsabile, calendario) e la si
       apre dal telefono, il sabato sera, senza passare da nessun'altra schermata. Il `.read` è quello
       giusto: è una pagina dei propri record (regola dell'albero qui sopra). */
    { label: 'Scambi turno', to: '/shift-swaps', icon: IconArrowsExchange, claim: 'ShiftSwapRequest.read' },
  ] },
  /* Quello che una persona **fa**, dal calendario alla rendicontazione. Le cinque tab di `/timesheet` non sono qui —
     le mostra il layout della sezione in testata (`timesheet/route.tsx`).

     ⚠️ Il `MobileNavDrawer` le tiene ancora, e **non è un disallineamento da correggere**: quel
     layout rende le tab solo su desktop (`isMobile ? <Outlet/> : <TimesheetLayout/>`), perché la
     schermata mobile ha il proprio header. Togliendole dal drawer, su telefono quelle pagine non
     avrebbero più nessuna strada. */
  { title: 'Il mio lavoro', icon: IconClipboardList, items: [
    { label: 'Pianificazione', to: '/planning', icon: IconCalendar, flag: 'planning.enabled', claim: 'PlanningEntry.read' },
    /* Il calendario del team serve a tutti (23-10-2026): «il collega è in sede, in smart, in ferie?».
       Sta qui e non in «Presenze & assenze», che è destinata a sparire. Chi non tratta dati sensibili
       vede «Assenza» al posto di permesso o malattia; l'HR lo trova qui, con i tipi.
       ⚠️ Non anche in «Risorse umane»: due voci sulla stessa pagina con permessi diversi fanno
       decidere la pagina alla prima (`entryByPath`), e quella dell'HR la chiuderebbe ai dipendenti. */
    { label: 'Calendario del personale', to: '/team-calendar', icon: IconCalendarStats, flag: 'leave.enabled', claim: 'LeaveRequest.read' },
    /*
     * `Task.read`, e non `ProjectTask.read`: sono due risorse diverse. La pagina legge `tasks`, la
     * cui classe CLR è `Task`; `ProjectTask` è il type `project-tasks`, i nodi di WBS di una
     * commessa. Il middleware confronta il nome della classe della risorsa chiesta, quindi
     * `ProjectTask.read` — che sul dev hanno undici profili — non ha mai aperto questa pagina: era
     * una voce che prometteva un 401 (misurato: `401 CLAIM_MISSING … GET su 'Task'`, anche per
     * TenantAdmin).
     *
     * ⚠️ E **non** `Task.readAll` come alternativa, benché tre profili lo abbiano: quello non apre
     * il modulo, governa la sesta voce del filtro — «Tutti». Senza `Task.read` il 401 arriva
     * comunque, verificato entrando con l'utenza demo dell'amministratore tenant.
     *
     * Oggi il claim non ce l'ha **nessuno**: il BE l'ha messo a catalogo (`TaskWorkBlock`, sul
     * profilo base) ma non è deployato, e serve `POST /api/roles/seed-claims`. Fino a quel momento
     * la voce la vede solo il super-admin — che è la verità, dato che a tutti gli altri la pagina
     * risponde 401.
     */
    // Da qui, in tab: per progetto, approvazione, causali, configurazione.
    /* ⚠️ Il `dot: true` che arrivava da main è stato tolto: i puntini del menu si **derivano**
       dalle segnalazioni del server, e uno scritto a mano qui è il difetto che Lares ha già
       pagato — venti voci col pallino acceso, e il segnale vero indistinguibile. Il flag del
       modulo invece resta: quello dice se la funzione è accesa per l'azienda. */
    { label: 'Rendicontazione', to: '/timesheet', icon: IconChartBar, flag: 'reporting.enabled', claim: 'TimesheetEntry.read' },
    { label: 'Rapportini intervento', to: '/service-reports', icon: IconFileText, flag: 'serviceReport.enabled', claim: 'ServiceReport.read' },
    // Sta col lavoro e non con le prenotazioni: la nota spese **raccoglie** ciò che è già
    // sulle giornate, e chi la cerca parte da lì.
    { label: 'Nota spese', to: '/expense-reports', icon: IconCoin, flag: 'reporting.enabled', claim: 'ExpenseReport.read' },
    // Abbonamenti del dipendente (BE 7 set 2026): trasporti, palestra, licenze software come
    // Claude, parcheggio. Sono un dato della persona, non di un modulo — per questo stanno qui
    // accanto alla nota spese e non sotto «Mezzi». Il claim di lettura lo ha il profilo base; il
    // perimetro (i miei, i miei sottoposti, tutti col readAll) lo applica il BE.
    { label: 'I miei abbonamenti', to: '/my-subscriptions', icon: IconCreditCard, flag: 'subscriptions.enabled', claim: 'ErpEmployeeSubscription.read' },
    /*
     * Le due pagine di chi guida un gruppo, dopo le voci personali (1921, 1904): chi è in ritardo nel
     * team, e le regole dell'azienda e dei gruppi. Stanno qui perché il responsabile apre «Il mio
     * lavoro» tutti i giorni, e «Organizzazione» mai.
     *
     * ⚠️ La visibilità è per **permesso**, non per dato: chi guida davvero un gruppo lo dice
     * `teamLeaderEmployee`, ma leggerlo vorrebbe dire una chiamata in più a ogni apertura del menu.
     * Scelto il 09-10-2026: chi ha il profilo responsabile e nessun gruppo apre la pagina e la trova
     * spiegata. E vale anche per l'indirizzo — il menu e la pagina hanno la stessa regola
     * (`navAccess.ts`).
     */
    /* «Scadenze del team» è salita in «Risorse umane» il 23-10-2026, col suo stesso permesso. */
    { label: 'Regole del gruppo', to: '/group-rules', icon: IconUsersGroup, claimAny: [...TEAM_LEAD_CLAIMS] },
    /* Le circolari ricevute (task 2055, 26-10-2026): per tutti. La casella non chiede claim
       (`circular-inbox` è self-service): ognuno legge le sue. Le note di rilascio no: hanno la loro
       pagina, `/circulars/release-notes`, e ci si arriva dal piede della pagina (`AppFooter`). */
    { label: 'Circolari', to: '/circulars', icon: IconSpeakerphone, alwaysVisible: true },
  ] },
  /* L'anagrafica su cui si lavora, che è una domanda diversa da «cosa devo fare oggi». */
  /*
   * LAVORO E ASSISTENZA — i task e i ticket, che sono la stessa materia vista da due lati.
   *
   * ⚠️ Il gruppo esiste in Lares con questo nome (`_customs/sidebarContents.js`, «LAVORO E
   * ASSISTENZA»), e con dentro le stesse cose: i task, i ticket, e la loro configurazione. Da
   * noi le tre pagine dell'assistenza stavano in «Configurazione», dove non c'entravano niente:
   * la scrivania è dichiarata nel suo codice come «una vista sui task», lo smistamento è lavoro
   * quotidiano di chi risponde, e i ticket sono ticket. Nessuna delle tre è un'impostazione.
   *
   * ⚠️ Due sezioni e non una lista piatta: un ticket **è** un task con l'argomento valorizzato,
   * e senza la divisione le sei voci si leggerebbero come sei cose scollegate. Lares le tiene in
   * fila; qui la fila si divide in due perché chi risponde ai ticket e chi lavora sui task sono
   * spesso due persone diverse.
   */
  /* Gli interruttori `tasks.enabled` e `workflows.enabled` (02-11-2026): spenti, il server rifiuta le
     scritture di task e flussi; i flussi dipendono dai task, e il menu lo sa da `me.modules`. */
  { title: 'Lavoro e Assistenza', icon: IconHeadset, groups: [
    { title: 'Task', icon: IconClipboardList, items: [
      { label: 'Task', to: '/tasks', icon: IconClipboardList, badgeKey: 'tasks', flag: 'tasks.enabled', claim: 'Task.read' },
      /* Gli appunti sono task (`kanbanState = 'note'`): stesso claim. Erano una scheda dei Task. */
      { label: 'Appunti', to: '/notes', icon: IconNotebook, flag: 'tasks.enabled', claim: 'Task.read' },
      /* In Lares sta qui e non in CONFIG: un flusso di lavorazione si cambia guardando i task
         che ci passano dentro, non fra le impostazioni del prodotto. `workflows` e' spento anche
         quando lo sono i task: lo calcola il BE. */
      { label: 'Flussi di lavorazione', to: '/admin/workflows', icon: IconGitBranch, flag: 'workflows.enabled', claim: 'WorkflowDefinition.update' },
      /* La bacheca: limiti di lavoro in corso, corsie, filtri di partenza. Sta accanto ai flussi
         perché le colonne le dichiara il flusso e i tetti per colonna questa pagina — chi cerca
         «perché la bacheca è fatta così» arriva a una delle due. Ci è arrivata con `main`, che
         l'aveva messa in «Config progetti & lavoro» accanto ai flussi: i flussi però da qui non
         si sono mossi, e separarle avrebbe rotto proprio il ragionamento del suo commento. */
      { label: 'Configurazione bacheca', to: '/admin/board-config', icon: IconLayoutKanban, flag: 'tasks.enabled', claim: 'KanbanBoardConfig.update' },
      /* Le etichette dei task, accanto a flussi e bacheca: un'etichetta può impostare cliente e
         progetto del task, quindi si configura guardando i task. Era in «Config progetti & lavoro». */
      { label: 'Etichette dei task', to: '/admin/task-labels', icon: IconTags, flag: 'tasks.enabled', claimAny: ['TaskLabel.create', 'TaskLabel.update'] },
      /* Le cinque regole configurabili del BE `v1.200.0` (task 1687 F4): chiusura con discendenti
         aperti, profondità dell'albero, tassonomia senza progetto, flusso obbligatorio, soglia
         degli avvisi. Sta con le altre due per la stessa ragione: il flusso dice quali stati
         esistono, la bacheca come si vedono, questa cosa succede quando si chiude un padre.
         Il claim è la **scrittura** e non `TenantSetting.read`, che sta nel profilo di chiunque
         lavori sui task (RoleClaimCatalog, `WorkflowReadBlock`): gatare sul `.read` di una pagina
         di configurazione è il controllo che sembra esserci e non c'è. */
      { label: 'Regole dei task', to: '/admin/task-rules', icon: IconAdjustments, flag: 'tasks.enabled', claim: 'TenantSetting.update' },
    ] },
    /*
     * ⚠️ QUATTRO VOCI DIVENTATE UNA, e non per gusto: dall'11-09-2026 l'assistenza **è** una
     * pagina sola.
     *
     * `/support` è un hub con tre schede — Scrivania (i ticket dei miei gruppi), Da smistare
     * (ciò che il server non ha saputo instradare), Argomenti (la configurazione: senza
     * argomenti la porta non è utilizzabile, perché l'argomento è obbligatorio). Qui c'erano le
     * tre voci separate più «Ticket», l'archivio del vecchio help desk: quattro posti per un
     * mestiere solo.
     *
     * Le rotte vecchie non sono sparite — `/support-intake` e `/admin/support-topics` rimandano
     * all'hub, `/tickets` resta come archivio — ma stanno in `navAccess.FUORI_MENU`: rispondono
     * a chi ci arriva da un segnalibro senza occupare una riga di menu.
     *
     * Il gate è l'unione di quelli delle tre schede; ogni scheda si smorza da sé per chi non ha
     * il suo permesso. I task nati dai ticket si lavorano da «Task», col perimetro «Il mio gruppo».
     */
    { title: 'Ticketing', icon: IconHeadset, items: [
      { label: 'Ticketing', to: '/support', icon: IconHeadset, flag: 'support.enabled', claimAny: ['support.intake.work', 'SupportIntake.read', 'SupportTopic.update'] },
    ] },
  ] },
  { title: 'Progetti & clienti', icon: IconBuildingFactory2, items: [
    { label: 'Progetti', to: '/projects', icon: IconBuildingFactory2, claimAny: ['ProjectEntity.read', 'r-p-projects'] },
    { label: 'Andamento WBS', to: '/projects/wbs', icon: IconSitemap, claimAny: ['ProjectEntity.read', 'r-p-projects'] },
    { label: 'Clienti', to: '/clients', icon: IconAddressBook, claim: 'Client.read' },
    /* Il portale clienti visto dall'azienda, accanto ai clienti (03-11-2026): cosa pubblichiamo a tutti.
       Prima era `/admin/portal-documents`, raggiungibile solo da Configurazione. I documenti di un
       cliente solo si pubblicano anche dalla sua scheda. */
    /* Chi, fra tutti i clienti, ha un accesso al portale (03-11-2026, come `/client-portal-access` di
       Lares): per rivedere o revocare senza aprire una scheda alla volta. Gli accessi si danno anche
       dalla scheda del cliente, dove la persona è già fra i contatti. */
    { label: 'Accessi al portale', to: '/client-portal-access', icon: IconKey, claim: 'client.portal.manage' },
    { label: 'Documenti del portale', to: '/client-portal-documents', icon: IconFolders, claim: 'client.portal.manage' },
    /* `canManageSprints` il BE lo **legge** (`SprintPermissions.cs`, `SprintImportController`) ma
       non lo **seeda**: non compare in nessuno dei 19 profili né nel seed legacy, quindi va
       assegnato a mano da un super-admin al reparto Sviluppo. Finché non lo si fa, questa voce la
       vede solo chi può già tutto — dichiarato qui e chiesto al BE. `Sprint.read` è la seconda
       strada, per dare la sola lettura senza dare l'amministrazione del modulo. */
    { label: 'Gestione Sprint', to: '/sprint', icon: IconTargetArrow, claimAny: ['canManageSprints', 'Sprint.read'], missingGate: 'canManageSprints' },
    /* «Magazzino» è passato in «Organizzazione DP» il 02-11-2026 (`setupSteps.ts`). */
  ] },
  { title: 'Prenotazioni & trasferte', icon: IconCalendarStats, items: [
    /* Basta uno dei tre moduli: il calendario legge auto, trasferte e sale ognuna col suo modulo
       (`features/bookings/bookingFeed.ts`), e con uno solo acceso mostra quello. Fino al 24-10-2026
       stava sotto `fleet.enabled`, e un'azienda con le sole sale non vedeva la voce. */
    { label: 'Prenotazioni', to: '/instruments-calendar', icon: IconCalendarStats, flagAny: ['fleet.enabled', 'travel.enabled', 'meetingRoom.enabled'], claimAny: ['MeetingRoomBooking.read', 'VehicleBooking.read', 'TravelBooking.read'] },
    /* «Approvazioni prenotazioni» (solo telefono, sulla coda vecchia) è stata tolta il 23-10-2026:
       le richieste di mezzi, trasferte e sale si approvano dalla sezione «Approvazioni», che il
       cassetto mostra già. Il vecchio indirizzo ci rimanda. */
    /* Le sale non hanno una voce propria: si prenotano da «Prenotazioni», come le auto —
       è lo stesso calendario, con il tipo «Sala». L'anagrafica (capienza, dotazioni,
       conferma) sta in Area segreteria, scheda «Sale»: è quella che riempie le select. */
    { label: 'Trasferte', to: '/travel', icon: IconPlaneTilt, flag: 'travel.enabled', claim: 'TravelBooking.read' },
  ] },
  /* I mezzi erano nello stesso gruppo delle prenotazioni: nove voci in cui «prenota una sala» e
     «gestisci la flotta» stavano alla stessa distanza. Sono due mestieri diversi. */
  { title: 'Mezzi', icon: IconCar, items: [
    { label: 'Mezzi', to: '/vehicles', icon: IconCar, flag: 'fleet.enabled', claim: 'Vehicle.read' },
    /* La flotta la amministra il profilo «Gestione parco auto» (`FleetManager`), collegato dal
       02-11-2026 (BE, task 3582). L'amministratore dell'azienda no, per scelta del BE: i veicoli
       sono dati del gestionale, e lui «non opera sui dati ERP». */
    { label: 'Flotta (gestione)', to: '/fleet', icon: IconCar, flag: 'fleet.enabled', claim: 'VehicleMaintenance.read' },
    { label: 'Prenotazioni mezzi', to: '/vehicle-bookings', icon: IconCar, flag: 'fleet.enabled', claim: 'VehicleBooking.read' },
    { label: 'Storico mezzi', to: '/car-history', icon: IconHistory, flag: 'fleet.enabled', claim: 'Vehicle.read' },
    // Non è flottato da `fleet.enabled`: il mezzo proprio esiste **perché** l'azienda non ha una
    // flotta da dare, quindi legarlo a quel flag lo nasconderebbe proprio a chi ne ha bisogno.
    // Il BE lo mette sotto la **rendicontazione** (`erp-employee-vehicles`, contratto §2): e' il
    // mezzo con cui si rimborsano i chilometri, non un veicolo aziendale.
    { label: 'Mezzo proprio', to: '/my-vehicles', icon: IconCar, flag: 'reporting.enabled', claim: 'ErpEmployeeVehicle.read' },
    // Dal 04-11-2026 il Dipendente ha il claim: il server filtra per responsabile.
    { label: 'I miei verbali', to: '/my-violations', icon: IconGavel, flag: 'fleet.enabled', claim: 'TrafficViolation.read', missingGate: 'TrafficViolation.read' },
  ] },
  /*
   * «Area segreteria» è una voce di primo livello dal 02-11-2026, al posto del vecchio gruppo
   * «Organizzazione». Lì stava anche «Segreteria & HR» (`/workforce`), tolta lo stesso giorno: le sue
   * schede erano doppie di «Organizzazione DP» (Dipendenti, Sedi, Gruppi) e di «Risorse umane» (Chi
   * c'è oggi, Formazione, Cruscotto HR), e le Sale sono passate qui.
   *
   * ⚠️ I claim sono quelli di **ogni scheda** (`secretaryTabs` in `features/secretary/secretaryQueues.ts`:
   * le due code, danni, mezzi e — `ROOMS_ADMIN_CLAIMS` — le sale), scritti qui e non importati: quel file si porta dietro
   * le API di flotta e trasferte, e il menu sta nel pacchetto che si scarica all'avvio (regola 19).
   */
  { title: 'Area segreteria', icon: IconBuildingCommunity, indexOnly: '/secretary/area', items: [
    { label: 'Area segreteria', to: '/secretary/area', icon: IconBuildingCommunity, claimAny: [
      'fleet.booking.assign.write', 'travel.booking.assign.write', 'VehicleIncident.update', 'Vehicle.update',
      'MeetingRoom.update', 'meetingRoom.booking.approve.write', ...HR_DESK_CLAIMS,
    ] },
  ] },
  { title: 'Vendite & fatturazione', icon: IconReceipt2, items: [
    { label: 'Ordini cliente', to: '/orders', icon: IconReceipt2, claim: 'SalesOrder.read' },
    /* «Abbonamento e pagamenti», non «Fatturazione»: `/billing` è l'abbonamento a MaeWay (Stripe —
       metodo di pagamento e fatture ricevute), non la fatturazione ai clienti. Col nome vecchio chi
       cercava le proprie fatture attive finiva lì (collaudo, 26-10-2026). */
    { label: 'Abbonamento e pagamenti', to: '/billing', icon: IconReceipt, claimAny: ['Invoice.read', 'billing.run', 'billing.settings'] },
    { label: 'Prodotti', to: '/products', icon: IconPackage, claimAny: ['ServiceItem.read', 'r-p-products-edit'] },
    { label: 'Licensing', to: '/licensing', icon: IconLicense, claim: 'TenantLicense.read' },
  ] },
  { title: 'Insights & report', icon: IconChartPie, items: [
    // `/api/insights/*` sta sotto la rendicontazione nel catalogo BE (§2), anche se aggrega pure assenze e presenze.
    { label: 'Insights', to: '/insights', icon: IconChartPie, flag: 'reporting.enabled', claimAny: ['TimesheetEntry.readAll', 'LeaveRequest.readAll', 'Attendance.readAll', 'isHR'] },
    { label: 'Intercompany', to: '/intercompany', icon: IconBuildingSkyscraper, claimAny: ['canSeeAllTenants', 'canManageSubTenants'] },
    /* Sviluppo **su misura** dello Studio Ferrari: non tocca il modello della piattaforma. È il
       modo in cui il custom convive col prodotto — un interruttore, non un fork. Dal 26-10-2026
       l'interruttore è un modulo del catalogo (`foodcost`, spento senza riga) e si legge da
       `me.modules` come gli altri: prima era l'unico che un'azienda poteva accendersi da sola. */
    { label: 'Controllo di gestione', to: '/foodcost', icon: IconReportAnalytics, flag: 'foodcost.enabled', custom: 'Studio Ferrari & Associati', alwaysVisible: true },
  ] },
  { title: 'Contenuti', icon: IconArticle, items: [
    { label: 'Articoli', to: '/articles', icon: IconArticle, claim: 'r-p-articles' },
    { label: 'Template', to: '/templates', icon: IconTemplate, claim: 'r-p-templates' },
    { label: 'Media', to: '/media', icon: IconPhoto, claim: 'r-p-media' },
    /*
     * ⚠️ Una voce sola, ed erano due.
     *
     * `/categories` (elenco piatto) e `/admin/categories` (albero) leggevano e scrivevano la
     * **stessa** risorsa `categories`, da due gruppi diversi del menu — l'albero stava sotto
     * «Portale & comunicazioni», dove col portale non c'entrava niente. Due pagine sullo stesso
     * dato non sono due viste: sono due posti dove correggere lo stesso difetto, e il giorno
     * che se ne corregge uno l'altro resta. L'elenco piatto è stato rimosso il 11-09-2026;
     * l'albero mostra la gerarchia, che era l'unica cosa che l'altro non sapeva fare.
     */
    { label: 'Categorie', to: '/admin/categories', icon: IconCategory, claim: 'r-p-categories' },
    { label: 'Traduzioni', to: '/translations', icon: IconLanguage, claimAny: ['Translation.update', 'canManageTranslation', 'r-p-translations'] },
  ] },
  /*
   * Amministrazione, in sette blocchi.
   *
   * Erano 42 voci in fila, cresciute una alla volta: molte sono la **configurazione di un
   * dominio** («config assenze», «policy trasferte», «preset commesse») e stavano qui perché
   * durante lo sviluppo era la strada più corta per arrivarci. Restano tutte — per una
   * trentina il menu è l'**unica** strada, e togliere la voce le renderebbe irraggiungibili
   * (è già successo con «Documenti del portale») — ma ognuna sta nel blocco del suo dominio.
   */
  /*
   * ⚠️ Era «Amministrazione», e nella colonna si apriva in quaranta righe: inutilizzabile.
   * Adesso è una voce sola che porta a `/configuration`, dove le stesse voci si vedono nelle
   * loro aree, tutte insieme. Il nome dice cosa sono — impostazioni del prodotto — invece di
   * dire chi le tocca.
   */
  { title: 'Configurazione', icon: IconAdjustments, indexOnly: '/configuration', masterOnly: true, groups: [
    { title: 'Utenti & permessi', icon: IconUserShield, items: [
      /* «Gestione utenti» (`/users`) non c'è più dal 01-11-2026: utenze e ruoli stanno in Organizzazione ›
         «Utenti e ruoli» (`/organization/permissions`), e l'indirizzo vecchio ci rinvia. */
      /* «Ruoli» (`/roles`) tolta il 02-11-2026: i ruoli si guardano in Organizzazione DP › «Utenti e ruoli». */
      // Non «Gestione tenant»: la pagina è il pannello di ruoli, claim CRUD, rotte e associazioni
      // utente↔tenant. Il nome vecchio prometteva l'anagrafica aziende, che è «Tenant (aziende)»
      // nel blocco qui sotto — due voci con lo stesso nome e due contenuti diversi.
      { label: 'Permessi, ruoli & rotte', to: '/admin/tenant-management', icon: IconUserShield, claimAny: ['RoleClaim.update', 'r-p-roles-permissions'] },
      // La vista multi-azienda delle associazioni utente↔azienda. Esiste da quando `user-tenants`
      // ha un perimetro server-side (BE 28 ago 2026): prima la stessa pagina si sarebbe potuta
      // scrivere solo leggendo le associazioni di tutte le aziende, che era il buco, non la funzione.
      { label: 'Utenti per azienda', to: '/admin/tenant-users', icon: IconUsers, claimAny: ['canSeeAllTenants', 'canManageSubTenants'] },
      { label: 'Audit impersonificazioni', to: '/user-audit', icon: IconUserShield, claimAny: ['ImpersonationLog.read', 'r-p-user-audit'] },
      { label: 'Accessi fra aziende', to: '/admin/cross-tenant-access-logs', icon: IconShieldLock, claim: 'CrossTenantAccessLog.read' },
    ] },
    /* «Config presenze & assenze» non c'è più dal 03-11-2026. Orari, punti di timbratura,
       promemoria e configurazione delle assenze erano già saliti in «Organizzazione DP»; l'ultima
       voce, «Policy aziendali», è sparita perché ritardi, smart working, pianificazione e calendario
       stanno tutti in «Regole di lavoro». `/admin/policies` resta e porta alla sezione giusta. */
    { title: 'Config progetti & lavoro', icon: IconBriefcase, items: [
      { label: 'Preset progetti', to: '/admin/project-type-presets', icon: IconBriefcase, claim: 'isSuperAdmin', missingGate: 'ProjectTypePreset.update' },
      { label: 'Campi custom progetto', to: '/admin/project-attribute-definitions', icon: IconClipboardList, claim: 'isSuperAdmin', missingGate: 'ProjectAttributeDefinition.update' },
      /* «Categorie progetti» è in «Regole di lavoro → Categorie progetto» dal 03-11-2026, e si gestisce
         anche dal campo «Categoria» del progetto. `/admin/project-categories` porta lì. */
      // Stati, transizioni e assegnazione ai gruppi (BE task 1400).
      /* ⚠️ «Flussi di lavorazione» e «Configurazione bacheca» stanno in «Lavoro e Assistenza →
         Task»: un flusso si cambia guardando i task che ci passano dentro, non fra le
         impostazioni del prodotto. Erano qui, e la stessa voce in due posti fa cercare due
         volte — `navGrouping` e `navParita` lo vietano, ed è il motivo per cui lo vietano. */
      /* «Etichette dei task» è salita in «Lavoro e Assistenza → Task» il 12-10-2026. */
      /* ⚠️ «Policy trasferte», «Tariffario km» e «Tipi di abbonamento» sono salite in
         «Organizzazione DP»: parlano di persone e di come si rimborsa il loro lavoro, non di
         progetti. I flag dei moduli li hanno seguite. */
    ] },
    /* ⚠️ Erano una sola area da otto voci — la più grande — e sono **due soggetti** tenuti
       insieme da una «&»: le aziende dell'installazione e le licenze del prodotto. Chi cerca il
       branding e chi cerca il catalogo licenze non stanno cercando la stessa cosa. */
    { title: 'Aziende', icon: IconBuildingSkyscraper, items: [
      { label: 'Elenco aziende', to: '/admin/tenants', icon: IconBuildingSkyscraper, claim: 'Tenant.update' },
      /*
       * ⚠️ **Superadmin, e non `TenantAttribute.update` — 06-10-2026.** È l'editor grezzo delle
       * righe chiave/valore: una chiave si scrive **per nome**, e un nome sbagliato non dà errore
       * — la riga si salva e non la legge nessuno. Finché era l'unico posto da cui accendere un
       * modulo valeva il rischio; da quando ogni chiave ha la sua schermata con il campo accanto
       * (presenze, promemoria, opzioni dei moduli), offrirla a chiunque amministri l'azienda vuol
       * dire offrirgli di scrivere a mano una cosa che ha già un posto giusto dove stare.
       *
       * Il claim governa tre cose insieme, ed è il motivo per cui si cambia **qui** e non nella
       * pagina: la voce nel menu e in `/configuration`, l'apertura della rotta (`RouteGate` risolve
       * il percorso su questa tabella) e i risultati di ⌘K.
       */
      /* ⚠️ Niente `missingGate`: quello nomina un claim che il BE non concede e che gli abbiamo
         chiesto. `TenantAttribute.update` lo concede eccome — è proprio chi ce l'ha che qui non
         deve passare. (`navGating` lo pretende, ed è il motivo per cui questa riga l'ha usato
         per sbaglio per dieci minuti.) */
      { label: 'Attributi azienda', to: '/admin/tenant-attributes', icon: IconAdjustments, claim: 'isSuperAdmin' },
      /* Gli interruttori dei dieci moduli, con il loro stato vero (BE-CONTRACT-TENANT-MODULES-v1 §3).
         Prima l'unico modo di spegnere la flotta era scrivere `fleet.enabled = false` a mano fra gli
         attributi, sapendo che esistesse; e dal 11-09 quello spegnimento chiude fuori **tutti**, quindi
         merita una pagina che dica cosa blocca prima di farlo. Stesso claim degli attributi: e' la
         stessa riga che si scrive. */
      { label: 'Moduli attivi', to: '/admin/modules', icon: IconToggleLeft, claim: 'TenantAttribute.update' },
      /* «Branding» (`/admin/branding`) tolta il 02-11-2026: si fa da Organizzazione DP › «Dati dell'azienda», sezione «Branding». */
      { label: 'Etichette struttura', to: '/admin/structure-labels', icon: IconList, claim: 'isSuperAdmin' },
      { label: 'Traduci struttura', to: '/admin/translate-structure', icon: IconLanguage, claimAny: ['Translation.update', 'canManageTranslation', 'r-p-translate-structure'] },
    ] },
    { title: 'Portale & comunicazioni', icon: IconWorld, items: [
      { label: 'Pagine di portale', to: '/admin/portal-pages', icon: IconLayoutGrid, claim: 'isSuperAdmin' },
      /* «Documenti del portale» dal 03-11-2026 sta in «Progetti & clienti» (`/client-portal-documents`). */
      /* Era «Invia comunicazioni», solo superadmin e solo e-mail. Dal 26-10-2026 una comunicazione è
         una circolare (task 2055): la scrive chi ha uno dei tre permessi delle circolari, in ogni
         azienda. Il vecchio indirizzo rimanda qui. */
      { label: 'Registro circolari', to: '/circulars/register', icon: IconSpeakerphone, claimAny: ['circular.write', 'circular.review', 'circular.publish'] },
    ] },
    /* ⚠️ Si chiamava «Assistenza & legali», e dell'assistenza non c'è più niente: è salita in
       «Lavoro e Assistenza», dove sta col lavoro di chi risponde invece che con le note legali.
       Un'area che nel nome promette una materia che non contiene è peggio di un'area piccola. */
    { title: 'Legali', icon: IconFileCertificate, items: [
      { label: 'Note legali', to: '/legal-notices', icon: IconScale, missingGate: 'Article.read' },
      // Non «Legali»: la pagina gestisce le versioni dei termini, ed è il posto da cui si
      // pubblica quello che «Note legali» fa leggere.
      { label: 'Termini & condizioni', to: '/legals', icon: IconFileCertificate, claimAny: ['LegalTerm.update', 'r-p-legals'] },
    ] },
    { title: 'Sistema & log', icon: IconSettings, items: [
      { label: 'Sistema', to: '/admin/system', icon: IconSettings, claim: 'CustomSetup.update' },
      { label: 'Manutenzione sistema', to: '/admin/maintenance', icon: IconRefresh, claimAny: ['canBypassMaintenance', 'CustomSetup.update'] },
      { label: 'Job schedulati', to: '/admin/jobs', icon: IconClockHour4, claim: 'isSuperAdmin' },
      { label: 'Audit log', to: '/admin/audit-log', icon: IconList, claimAny: ['ImpersonationLog.read', 'ErpAuditLog.read'] },
      /* Solo la piattaforma (deciso il 01-11-2026): gli endpoint dei log vogliono `NeededRoleLevel10`,
         che apriva solo il ruolo storico Admin, che si elimina (`docs/FE-TO-BE-ELIMINARE-RUOLO-ADMIN-v1.md`
         §3). Fino a quel giorno la voce si apriva anche con `LogFilterPreset.read`, che ha
         l'amministratore dell'azienda: la vedeva, e i registri gli rispondevano di no. */
      { label: 'Log applicativi', to: '/admin/logs', icon: IconListSearch, claim: 'isSuperAdmin' },
      /* «Connettori gestionale» e non «Integrazioni»: quel nome c'e' gia' in Account, ed e' il
         consenso della persona ai servizi esterni (`/settings/integrations`). Qui sta l'agente che
         parla col gestionale di un'azienda, in sola lettura. I quattro claim sono le quattro
         schede: basta leggerne una per entrare, e il BE oggi li da' al solo profilo
         «Amministrazione» (`BillingReadBlock`). */
      { label: 'Connettori gestionale', to: '/admin/connectors', icon: IconPlugConnected, claimAny: ['isSuperAdmin', 'Connector.read', 'ConnectorRun.read', 'ConnectorCommand.read', 'IntegrationOutbox.read'] },
      /* Gli assistenti AI (Claude, via server MCP) collegati da tutta l'azienda, con la revoca
         (`docs/FE-TO-BE-MCP-v1.md` §3). I propri ognuno li ha in Impostazioni › Integrazioni. */
      { label: 'Assistenti AI collegati', to: '/admin/mcp-connections', icon: IconRobot, claimAny: ['isSuperAdmin', 'mcp.manage'], flag: 'mcp.enabled' },
      /* «Onboarding guidato» fino al 02-11-2026: la lista di controllo è uscita (la sostituisce il percorso
         guidato di «Dati dell'azienda»), resta la creazione delle aziende dei clienti, che è del Master. */
      { label: 'Nuova azienda', to: '/onboarding', icon: IconRocket, claim: 'isSuperAdmin' },
      { label: 'Caricamento di avvio', to: '/admin/client-intake', icon: IconFileSpreadsheet, claimAny: ['ErpEmployee.create', 'r-p-setup'] },
    ] },
    /* ⚠️ **Ultima di proposito.** Oggi le licenze sono la cosa meno toccata di questa pagina, e
       l'ordine delle aree è l'unico modo che una panoramica ha di dire cosa conta di più. Il
       giorno che contano, si spostano su: è una riga. */
    { title: 'Licenze', icon: IconLicense, items: [
      /* Piani e catalogo licenze sono il listino della piattaforma: restano del super-admin per scelta,
         non per un claim che manca (22-10-2026). Le funzioni per azienda le governano i moduli. */
      { label: 'Piani licenza', to: '/admin/license-plans', icon: IconLicense, claim: 'isSuperAdmin' },
      { label: 'Catalogo licenze', to: '/admin/licenses-catalog', icon: IconLicense, claim: 'isSuperAdmin' },
      { label: 'Dashboard licenze', to: '/admin/license-dashboard', icon: IconGauge, claim: 'TenantLicense.read' },
    ] },
  ] },
  { title: 'Qualità & collaudo', icon: IconFlask, items: [
    { label: 'Campagne di collaudo', to: '/test-plans', icon: IconFlask, claim: 'TestPlan.read' },
    { label: 'Cruscotto collaudo', to: '/test-coverage', icon: IconChartDonut, claim: 'TestPlan.read' },
    { label: 'Catalogo flussi', to: '/test-cases', icon: IconClipboardCheck, claim: 'TestCase.read' },
  ] },
  { title: 'Aiuto & documentazione', icon: IconLifebuoy, items: [
    { label: 'Aiuto', to: '/help', icon: IconLifebuoy, alwaysVisible: true },
    { label: 'Guida utente', to: '/user-guide', icon: IconBook, alwaysVisible: true },
    { label: 'Guida sviluppatore', to: '/developer-guide', icon: IconBook, claimAny: ['isDeveloper', 'r-p-developer-guide'] },
    { label: 'Chi siamo', to: '/about', icon: IconInfoCircle, alwaysVisible: true },
    { label: 'App Android', to: '/app-android', icon: IconBrandAndroid, alwaysVisible: true },
  ] },
  { title: 'Impostazioni', icon: IconSettings, items: [
    { label: 'Impostazioni', to: '/settings', icon: IconSettings, alwaysVisible: true },
  ] },
];

/*
 * ⚠️ LA VOCE ACCESA È QUELLA DALL'INDIRIZZO PIÙ PRECISO, non ogni voce di cui l'indirizzo è un prefisso.
 *
 * La regola era «l'indirizzo comincia col tuo»: su `/organization/sites` si accendevano «Sedi» e anche
 * «Dipendenti», che è `/workforce` — due voci accese dicono che si è in due posti. Ora una voce
 * resta accesa sulle sue pagine interne (`/workforce/<id>` è ancora «Dipendenti») solo se nessun'altra
 * voce del menu le descrive meglio. Vale per la colonna e per il cassetto, che la leggono da qui.
 */
const NAV_TARGETS: readonly string[] = SECTIONS.flatMap((sec) => [
  ...sectionItems(sec).map((it) => it.to),
  ...(sec.indexOnly ? [sec.indexOnly] : []),
]);

const covers = (to: string, path: string) => path === to || path.startsWith(`${to}/`);

/**
 * I gruppi che il menu mostra su questa azienda. `onMasterTenant` falso toglie quelli `masterOnly`.
 * Colonna desktop e cassetto del telefono passano di qui (`useShownSections`), così non divergono.
 */
export function sectionsShown(sections: readonly NavSection[], onMasterTenant: boolean): NavSection[] {
  return sections.filter((sec) => !sec.masterOnly || onMasterTenant);
}

export function isNavActive(to: string, path: string): boolean {
  if (to === '/') return path === '/';
  if (!covers(to, path)) return false;
  return !NAV_TARGETS.some((t) => t !== '/' && t.length > to.length && covers(t, path));
}


/**
 * Il gruppo contiene la pagina in cui si è (01-11-2026)? Per un gruppo «a indice» (Configurazione) vale
 * anche quando si è su una delle pagine che raccoglie, non solo sulla pagina indice: prima si
 * accendeva su `/configuration` e restava spento su `/admin/system`, cioè proprio quando serviva.
 * `items` sono le voci che chi guarda vede: un gruppo non si accende per una pagina che non gli offre.
 */
export function sectionHoldsPath(sec: NavSection, items: readonly NavItem[], path: string): boolean {
  if (sec.indexOnly && isNavActive(sec.indexOnly, path)) return true;
  return items.some((it) => isNavActive(it.to, path));
}

/**
 * Il gruppo da tenere aperto su questa pagina: quello che la contiene, oppure nessuno. Il menu è a
 * fisarmonica, quindi cambiando pagina si apre il gruppo nuovo e si chiude ogni altro; su una pagina
 * senza voce si chiude tutto. Un gruppo a indice non si apre (le sue voci non stanno in colonna).
 */
export function sectionToOpen(sections: readonly NavSection[], visible: (it: NavItem) => boolean, path: string): string | null {
  for (const sec of sections) {
    if (!sec.title || sec.indexOnly) continue;
    if (sectionHoldsPath(sec, sectionItems(sec).filter(visible), path)) return sec.title;
  }
  return null;
}
